import React, { useContext, useMemo, useState } from 'react';
import { Search, Package, Loader2, AlertTriangle, X, Pin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tab, Product } from '@/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useIvaPricing } from '@/hooks/useIvaPricing';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import StagedProductsList from './products/StagedProductsList';
import { useStagedProductsStore } from '@/stores/stagedProductsStore';
import { useCommitStagedProducts } from '@/hooks/tabs/useCommitStagedProducts';
import { useOrganization } from '@/hooks/useOrganization';
import { useProducts } from '@/hooks/useProducts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { usePromotions } from '@/hooks/usePromotions';
import { PromoSelectionDialog } from '@/components/promotions/PromoSelectionDialog';
import WeightInputDialog from '@/components/products/WeightInputDialog';
import { useIsBelowLg } from '@/hooks/use-mobile';
import { isProductAvailableToday } from '@/utils/productWeekdayAvailability';
import { useAuth } from '@/hooks/useAuth';
import ProductQueueListSheet from './tabs/ProductQueueListSheet';
import { cn, isQuickSaleTab } from '@/lib/utils';
import { useFlashFeedback } from '@/hooks/useFlashFeedback';

interface ProductCatalogProps {
  selectedTab: Tab;
  onCloseClick: () => void;
  onPayClick: () => void;
}

const ProductCatalog: React.FC<ProductCatalogProps> = ({ selectedTab, onCloseClick, onPayClick }) => {
  const { formatAmount } = useCurrency();
  const { showIvaPrice, getPriceWithIva } = useIvaPricing();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const { translator } = useContext(LanguageProviderContext);
  const { currentOrganization } = useOrganization();
  const { commitStagedProducts } = useCommitStagedProducts();
  const { productsForSales } = useProducts();
  const { isEnabled } = useFeatureFlag();
  const promoProductsEnabled = isEnabled(FEATURE_FLAGS.PROMOTIONS);
  const weekdayAvailabilityEnabled = isEnabled(FEATURE_FLAGS.WEEKDAY_AVAILABILITY);
  const soldByWeightEnabled = isEnabled(FEATURE_FLAGS.SOLD_BY_WEIGHT);
  const { activePromoProductIdSet, getActivePromotionForPromoProduct } = usePromotions();
  const isBelowLg = useIsBelowLg();
  const { profile, simpleUser } = useAuth();
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);
  const [promoPickProduct, setPromoPickProduct] = useState<Product | null>(null);
  const [weightPickProduct, setWeightPickProduct] = useState<Product | null>(null);
  const { flash: flashProductCard, isFlashing, handleAnimationEnd } = useFlashFeedback();

  const handleAddProduct = (product: Product) => {
    if (promoProductsEnabled && activePromoProductIdSet.has(product.id)) {
      setPromoPickProduct(product);
      return;
    }

    if (soldByWeightEnabled && product.isSoldByWeight) {
      setWeightPickProduct(product);
      return;
    }

    stageProduct(product);
    flashProductCard(product.id);
  };

  const productNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of productsForSales) m.set(p.id, p.name);
    return m;
  }, [productsForSales]);

  const promoDefinitionForPick = promoPickProduct
    ? getActivePromotionForPromoProduct(promoPickProduct.id)
    : null;

  const orgTimezone = currentOrganization?.timezone;
  const productsForSalesToday = useMemo(
    () =>
      weekdayAvailabilityEnabled
        ? productsForSales.filter((p) => isProductAvailableToday(p, orgTimezone))
        : productsForSales,
    [productsForSales, orgTimezone, weekdayAvailabilityEnabled]
  );
  // Use staged products store
  const stagedProducts = useStagedProductsStore((state) => state.stagedProducts);
  const editingNotesId = useStagedProductsStore((state) => state.editingNotesId);
  const isCommitting = useStagedProductsStore((state) => state.isCommitting);
  const stageProduct = useStagedProductsStore((state) => state.stageProduct);
  const decreaseQuantity = useStagedProductsStore((state) => state.decreaseQuantity);
  const increaseQuantity = useStagedProductsStore((state) => state.increaseQuantity);
  const setQuantity = useStagedProductsStore((state) => state.setQuantity);
  const setWeight = useStagedProductsStore((state) => state.setWeight);
  const toggleNotes = useStagedProductsStore((state) => state.toggleNotes);
  const updateNotes = useStagedProductsStore((state) => state.updateNotes);
  const updateCustomerPosition = useStagedProductsStore((state) => state.updateCustomerPosition);
  
  const categories = useMemo(
    () => ['All', ...Array.from(new Set(productsForSalesToday.map((p) => p.category)))],
    [productsForSalesToday]
  );

  const filteredProducts = useMemo(
    () =>
      productsForSalesToday
        .filter((product) => {
          const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
          const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
          const isEnabled = product.isEnabled !== false;
          return matchesCategory && matchesSearch && isEnabled;
        })
        .sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return 0;
        }),
    [selectedCategory, searchTerm, productsForSalesToday]
  );

  const totalQueued = stagedProducts.reduce((total, entry) => total + entry.quantity, 0);
  const isQuickSale = isQuickSaleTab(selectedTab, translator('Tabs.quickSale'));

  const handleToggleNotes = (lineId: string, hasExistingNotes: boolean) => {
    toggleNotes(lineId);
  };

  const handleCommit = async () => {
    try {
      const serverName = selectedTab.serverName || simpleUser?.username || profile?.full_name || undefined;
      await commitStagedProducts({
        tabId: selectedTab.id,
        customerName: selectedTab.customerName,
        tableId: selectedTab.tableId,
        serverName,
        onComplete: onCloseClick,
        getPromotionForProduct: getActivePromotionForPromoProduct,
        productNameById,
      });
    } catch (error) {
      console.error('ProductCatalog: Failed to add staged products to tab', error);
    }
  };

  const renderButton = (widthClassName = 'w-1/2 sm:w-full') => {
    if (isQuickSale) {
      return (
        <Button
          className={cn(widthClassName, 'bg-green-600 hover:bg-green-700 text-white')}
          onClick={onPayClick}
          disabled={isCommitting}
        >
          {isCommitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {translator('ProductsCatalog.adding')}
            </>
          ) : (
            <>
              {translator('Tabs.pay')}
            </>
          )}
        </Button>
      );
    } else {
      return (
        <Button
          className={widthClassName}
          onClick={handleCommit}
          disabled={stagedProducts.length === 0 || isCommitting}
        >
          {isCommitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {translator('ProductsCatalog.adding')}
            </>
          ) : (
            <>
              {translator('ProductsCatalog.addToTab')}
            </>
          )}
        </Button>
      );
    }
  }

  return (
    <>
    <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)] flex-1 min-h-0 overflow-hidden">
      <div className="min-w-0 space-y-0 relative flex flex-col h-full overflow-hidden">
        {/* Bottom Shadow Overlay */}
        <div
          className="pointer-events-none absolute left-0 bottom-0 w-full"
          style={{
            height: '20px',
            zIndex: 10,
            // a more pronounced/blurred dark to transparent fade, tweak as needed
            background: 'linear-gradient(to top, rgba(80,85,120,0.16), transparent)'
          }}
        />
      
 
      {/* Search and Filter */}
        <div className="min-w-0 space-y-4 shrink-0 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder={translator('ProductsCatalog.searchProducts')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted"
                onClick={() => setSearchTerm('')}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
          
          <div className="w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain touch-pan-x pb-1 md:overflow-visible md:overscroll-auto md:touch-auto">
            <div className="flex w-max flex-nowrap gap-2 pr-1 md:w-full md:flex-wrap md:pr-0">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  className="shrink-0"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'All' ? translator('ProductCategories.All') : category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="min-w-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y pr-1 pb-4">
          {filteredProducts.length > 0 ? (
            <div className="grid min-w-0 grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-3">
              {filteredProducts.map(product => (
                <Card
                  key={product.id}
                  className={cn(
                    // Hover lifts the card, press pushes it in. Previously hover
                    // shrank the card while its shadow grew, so the two depth
                    // cues pointed opposite ways.
                    'relative h-fit cursor-pointer select-none border transition-[transform,box-shadow] duration-150 hover:shadow-md active:scale-[0.98] active:shadow-sm motion-reduce:transition-none motion-reduce:active:scale-100',
                    isFlashing(product.id) && 'animate-product-staged-flash',
                  )}
                  onAnimationEnd={(event) => handleAnimationEnd(product.id, event)}
                >
                  {product.stock <= 0 && !product.avoidTracking && (
                    <Badge variant="outline" className="absolute top-0 right-0 text-xs bg-red-50 text-red-600 border-red-200 z-10 rounded-none rounded-bl-lg border-t-0 border-r-0">
                      <X className="h-4 w-4" />
                    </Badge>
                  )}
                  {product.stock > 0 && product.minimumStockLevel && product.stock <= product.minimumStockLevel && !product.avoidTracking && (
                    <Badge variant="outline" className="absolute top-0 right-0 text-xs bg-amber-50 text-amber-600 border-amber-200 z-10 rounded-none rounded-bl-lg border-t-0 border-r-0">
                      <AlertTriangle className="h-4 w-4" />
                    </Badge>
                  )}
                  
                  <CardHeader className="p-3 pb-0 h-20">
                    <CardTitle className="text-base font-medium flex items-start gap-1 flex-wrap">
                      {product.isPinned && (
                        <span className="flex items-center justify-center h-6 w-6 rounded-md bg-amber-100 dark:bg-amber-900/40 shrink-0">
                          <Pin className="h-4 w-4 text-amber-500" />
                        </span>
                      )}
                      {product.name}
                      {promoProductsEnabled && activePromoProductIdSet.has(product.id) && (
                        <Badge variant="default" className="ml-1 self-center text-[10px] px-1.5 py-0 shrink-0">
                          {translator('Promotions.bundleBadge')}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>{product.category}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <Badge variant="secondary" className="w-fit">
                          {formatAmount(product.price)}
                          {soldByWeightEnabled && product.isSoldByWeight
                            ? ` ${translator('Products.pricePerKgBadge')}`
                            : ''}
                        </Badge>
                        {showIvaPrice && (
                          <span className="text-[10px] leading-tight text-muted-foreground truncate">
                            {formatAmount(getPriceWithIva(product.price))} {translator('Global.ivaIncluded')}
                            {soldByWeightEnabled && product.isSoldByWeight
                              ? ` ${translator('Products.pricePerKgBadge')}`
                              : ''}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddProduct(product);
                        }}
                        disabled={isCommitting}
                      >
                        {translator('ProductsCatalog.add')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">{translator('ProductsCatalog.noProductsFound')}</h3>
              <p className="text-muted-foreground">
                {searchTerm || selectedCategory !== 'All'
                  ? translator('ProductsCatalog.ajustSearchCriteria')
                  : translator('ProductsCatalog.noProductsAvailable')}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="min-w-0 space-y-4 flex flex-col h-full overflow-hidden shrink-0 lg:shrink">
        {isBelowLg ? (
          <>
            <div className="flex w-full gap-2">
              <Button
                type="button"
                variant="outline"
                className="min-w-0 flex-1 basis-0"
                onClick={() => setQueueDrawerOpen(true)}
              >
                <span className="truncate">{translator('Tabs.queueItems')}</span>
                <span className="ml-1 shrink-0 text-green-500">({totalQueued})</span>
              </Button>
              {renderButton('min-w-0 flex-1 basis-0')}
            </div>
            <ProductQueueListSheet
              open={queueDrawerOpen}
              onOpenChange={setQueueDrawerOpen}
              title={translator('ProductsCatalog.stagedItemsTitle')}
              totalQueued={totalQueued}
              queueDrawerOpen={queueDrawerOpen}
              setQueueDrawerOpen={setQueueDrawerOpen}
              footer={renderButton('w-full')}
            >
              <StagedProductsList
                stagedProducts={stagedProducts}
                isCommitting={isCommitting}
                onDecreaseQuantity={decreaseQuantity}
                onIncreaseQuantity={increaseQuantity}
                onSetQuantity={setQuantity}
                onSetWeight={setWeight}
                onToggleNotes={handleToggleNotes}
                onNotesChange={updateNotes}
                onSetCustomerPosition={updateCustomerPosition}
                scrollContainerClassName="flex-1 min-h-0 h-full"
              />
            </ProductQueueListSheet>
          </>
        ) : (
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-md">
                {translator('ProductsCatalog.stagedItemsTitle')}
                <span className="text-green-500"> ({totalQueued})</span>
              </CardTitle>
            </CardHeader>
            <CardContent
              className="space-y-4 flex flex-col justify-between"
              style={{ height: 'calc(100% - 60px)' }}
            >
              <StagedProductsList
                stagedProducts={stagedProducts}
                isCommitting={isCommitting}
                onDecreaseQuantity={decreaseQuantity}
                onIncreaseQuantity={increaseQuantity}
                onSetQuantity={setQuantity}
                onSetWeight={setWeight}
                onToggleNotes={handleToggleNotes}
                onNotesChange={updateNotes}
                onSetCustomerPosition={updateCustomerPosition}
              />
              {renderButton()}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    {promoProductsEnabled && promoPickProduct && promoDefinitionForPick && (
      <PromoSelectionDialog
        open
        onOpenChange={(o) => {
          if (!o) setPromoPickProduct(null);
        }}
        definition={promoDefinitionForPick}
        bundleProduct={promoPickProduct}
        productNameById={productNameById}
        onConfirm={(selections) => {
          stageProduct(promoPickProduct, selections);
          flashProductCard(promoPickProduct.id);
          setPromoPickProduct(null);
        }}
      />
    )}
    {soldByWeightEnabled && weightPickProduct && (
      <WeightInputDialog
        open
        onOpenChange={(o) => {
          if (!o) setWeightPickProduct(null);
        }}
        product={weightPickProduct}
        onConfirm={(weightKg) => {
          stageProduct(weightPickProduct, undefined, weightKg);
          flashProductCard(weightPickProduct.id);
          setWeightPickProduct(null);
        }}
      />
    )}
    </>
  );
};

export default ProductCatalog;
